// batch-66 contract-mijlpalen (FO-496) — Types, DTO's en Zod-schemas voor de
// mijlpaal-workflow rond het arbeidscontract: van ondertekening tot contracteinde.
//
// Kern: een (per contracttype/BV instelbaar) sjabloon met de reeks M1 t/m M9
// levert per contract een rij `ContractMijlpaal` per code. De PURE planner
// (`berekenMijlpalen` in `@streep/utils`) rekent de momenten uit; de motor
// (`apps/api/src/services/contract-mijlpalen.ts`) zet taken, meldingen en
// gesprekken klaar. Geen salarisbedragen in DTO's of audit (zelfde regel als
// FO-388): de workflow gaat over momenten en gesprekken, niet over geld.
//
// Datums gaan over de API als 'yyyy-mm-dd' (NL-kalenderdag). In de DB staan ze
// als UTC-middernacht van die kalenderdag, zoals de overige contractdatums.

import { z } from 'zod'
import type { NotificatieEventTypeKey } from './notificaties'
import type { ContractVerlengingBesluitType } from './contract-verlenging'

// ── Enums (mirror de Prisma-enums) ───────────────────────────────────────────

// batch-71 kennismaking (FO-534): M2B = de kennismaking voor de start. M2 blijft de
// melding; zo blijven `herhaalGemisteMeldingen` en `werkTakenBij` ongemoeid.
export const CONTRACT_MIJLPAAL_CODES = ['M1', 'M2', 'M2B', 'M3', 'M4', 'M5', 'M6', 'M7', 'M8', 'M9'] as const
export type ContractMijlpaalCode = (typeof CONTRACT_MIJLPAAL_CODES)[number]

export const CONTRACT_MIJLPAAL_ANKERS = [
  'ONDERTEKEND',
  'START',
  'EINDE_WEEK_1',
  'EINDE_MAAND_1',
  'PROEFTIJD_EINDE',
  'CONTRACT_EINDE',
] as const
export type ContractMijlpaalAnker = (typeof CONTRACT_MIJLPAAL_ANKERS)[number]

export const CONTRACT_MIJLPAAL_OFFSET_EENHEDEN = ['DAGEN', 'WEKEN', 'MAANDEN', 'WERKDAGEN'] as const
export type ContractMijlpaalOffsetEenheid = (typeof CONTRACT_MIJLPAAL_OFFSET_EENHEDEN)[number]

export const CONTRACT_MIJLPAAL_ACTIES = [
  'MELDING',
  'TAAK',
  'GESPREK_PLANNEN',
  'VERSLAG_VERPLICHT',
] as const
export type ContractMijlpaalActie = (typeof CONTRACT_MIJLPAAL_ACTIES)[number]

export const CONTRACT_MIJLPAAL_STATUSSEN = [
  'GEPLAND',
  'OPEN',
  'AFGEROND',
  'OVERGESLAGEN',
  'VERLOPEN',
] as const
export type ContractMijlpaalStatus = (typeof CONTRACT_MIJLPAAL_STATUSSEN)[number]

/** Statussen waarin een mijlpaal nog werk vraagt (de werkvoorraad van het overzicht). */
export const CONTRACT_MIJLPAAL_OPEN_STATUSSEN = ['OPEN', 'VERLOPEN'] as const satisfies readonly ContractMijlpaalStatus[]

/** Statussen die bij een herberekening blijven staan (nooit meer verschuiven). */
export const CONTRACT_MIJLPAAL_EIND_STATUSSEN = ['AFGEROND'] as const satisfies readonly ContractMijlpaalStatus[]

export const CONTRACT_MIJLPAAL_DOELGROEPEN = [
  'HR',
  'TEAMLEIDER',
  'DIRECTIE',
  'FACILITIES',
  'RECRUITMENT',
] as const
export type ContractMijlpaalDoelgroep = (typeof CONTRACT_MIJLPAAL_DOELGROEPEN)[number]

export const CONTRACT_MIJLPAAL_WERKDAG_CORRECTIES = [
  'GEEN',
  'VORIGE_WERKDAG',
  'VOLGENDE_WERKDAG',
] as const
export type ContractMijlpaalWerkdagCorrectie = (typeof CONTRACT_MIJLPAAL_WERKDAG_CORRECTIES)[number]

/**
 * Waarom een mijlpaal is overgeslagen. De eerste vijf leidt de planner af uit de
 * contractgegevens en het sjabloon (ze vervallen weer als die wijzigen).
 * MOMENT_VERSTREKEN = het moment was al voorbij toen de reeks startte; MIGRATIE =
 * idem bij de eenmalige vulling voor lopende contracten; BEEINDIGD = de reeks is
 * gestopt; HANDMATIG = door HR, met verplichte toelichting.
 */
export const CONTRACT_MIJLPAAL_OVERSLAAN_REDENEN = [
  'ONBEPAALDE_TIJD',
  'GEEN_PROEFTIJD',
  'VERLENGING',
  'NIET_ONDERTEKEND',
  'NIET_IN_SJABLOON',
  'MOMENT_VERSTREKEN',
  'MIGRATIE',
  'BEEINDIGD',
  'HANDMATIG',
] as const
export type ContractMijlpaalOverslaanReden = (typeof CONTRACT_MIJLPAAL_OVERSLAAN_REDENEN)[number]

/** Redenen die de planner afleidt; die mogen bij een herberekening weer vervallen. */
export const CONTRACT_MIJLPAAL_AFGELEIDE_OVERSLAAN_REDENEN = [
  'ONBEPAALDE_TIJD',
  'GEEN_PROEFTIJD',
  'VERLENGING',
  'NIET_ONDERTEKEND',
  'NIET_IN_SJABLOON',
] as const satisfies readonly ContractMijlpaalOverslaanReden[]

/**
 * STANDAARD = de reeks zoals geseed. GEEN_PROEFTIJD = het contract kent geen
 * proeftijd: de week-1-check vraagt dan een evaluatiegesprek na een maand in
 * plaats van het einde-proeftijdgesprek (spec §5.3).
 */
export const CONTRACT_MIJLPAAL_VARIANTEN = ['STANDAARD', 'GEEN_PROEFTIJD'] as const
export type ContractMijlpaalVariant = (typeof CONTRACT_MIJLPAAL_VARIANTEN)[number]

/** Fase voor het filter in het HR-overzicht (spec §2.D.7). */
export const CONTRACT_MIJLPAAL_FASES = ['START', 'PROEFTIJD', 'VERLENGING'] as const
export type ContractMijlpaalFase = (typeof CONTRACT_MIJLPAAL_FASES)[number]

export const CONTRACT_MIJLPAAL_FASE_PER_CODE: Record<ContractMijlpaalCode, ContractMijlpaalFase> = {
  M1: 'START',
  M2: 'START',
  M2B: 'START',
  M3: 'START',
  M4: 'START',
  M5: 'PROEFTIJD',
  M6: 'PROEFTIJD',
  M7: 'PROEFTIJD',
  M8: 'PROEFTIJD',
  M9: 'VERLENGING',
}

/** Welk formulier hoort bij een mijlpaal (null = geen formulier). */
export const CONTRACT_MIJLPAAL_FORMULIEREN = ['CHECKIN', 'OVERLEG_PROEFTIJD'] as const
export type ContractMijlpaalFormulierSoort = (typeof CONTRACT_MIJLPAAL_FORMULIEREN)[number]

export const CONTRACT_MIJLPAAL_FORMULIER_PER_CODE: Record<
  ContractMijlpaalCode,
  ContractMijlpaalFormulierSoort | null
> = {
  M1: null,
  M2: null,
  M2B: null,
  M3: null,
  M4: null,
  M5: 'CHECKIN',
  M6: 'CHECKIN',
  M7: 'OVERLEG_PROEFTIJD',
  M8: null,
  M9: null,
}

/** Uitkomst van het proeftijdoverleg (M7, voorstel) en van het proeftijdgesprek (M8). */
export const CONTRACT_PROEFTIJD_UITKOMSTEN = [
  'VOORTZETTEN',
  'BEEINDIGEN',
  'VERLENGEN_AANDACHTSPUNTEN',
] as const
export type ContractProeftijdUitkomst = (typeof CONTRACT_PROEFTIJD_UITKOMSTEN)[number]

/** M8 kent alleen voortzetten of beeindigen: aandachtspunten zijn een voorstel, geen uitkomst. */
export const CONTRACT_PROEFTIJD_EINDUITKOMSTEN = ['VOORTZETTEN', 'BEEINDIGEN'] as const satisfies readonly ContractProeftijdUitkomst[]
export type ContractProeftijdEinduitkomst = (typeof CONTRACT_PROEFTIJD_EINDUITKOMSTEN)[number]

export const CONTRACT_MIJLPAAL_AUDIT_ACTIES = [
  'AANGEMAAKT',
  'GEOPEND',
  'AFGEROND',
  'OVERGESLAGEN',
  'VERLOPEN',
  'GEESCALEERD',
  'VERSCHOVEN',
  'HEROPEND',
  'HERSTELD',
  'CORRECTIE_VERZONDEN',
  'GESPREK_GEKOPPELD',
  'FORMULIER_OPGESLAGEN',
  'CHECKLIST_GEWIJZIGD',
  'TEAMLEIDER_GEWIJZIGD',
  'REEKS_GESTOPT',
  'REEKS_HERVAT',
  'OFFBOARDING_GESTART',
] as const
export type ContractMijlpaalAuditActie = (typeof CONTRACT_MIJLPAAL_AUDIT_ACTIES)[number]

// ── Labels (NL voor UI) ──────────────────────────────────────────────────────

export const CONTRACT_MIJLPAAL_ANKER_LABELS: Record<ContractMijlpaalAnker, string> = {
  ONDERTEKEND:     'Ondertekening',
  START:           'Startdatum',
  EINDE_WEEK_1:    'Einde week 1 (vrijdag)',
  EINDE_MAAND_1:   'Einde maand 1',
  PROEFTIJD_EINDE: 'Einde proeftijd',
  CONTRACT_EINDE:  'Einde contract',
}

export const CONTRACT_MIJLPAAL_OFFSET_EENHEID_LABELS: Record<ContractMijlpaalOffsetEenheid, string> = {
  DAGEN:   'dagen',
  WEKEN:   'weken',
  MAANDEN: 'maanden',
  WERKDAGEN: 'werkdagen',
}

export const CONTRACT_MIJLPAAL_ACTIE_LABELS: Record<ContractMijlpaalActie, string> = {
  MELDING:           'Melding',
  TAAK:              'Taak',
  GESPREK_PLANNEN:   'Gesprek plannen',
  VERSLAG_VERPLICHT: 'Verslag verplicht',
}

export const CONTRACT_MIJLPAAL_STATUS_LABELS: Record<ContractMijlpaalStatus, string> = {
  GEPLAND:      'Gepland',
  OPEN:         'Open',
  AFGEROND:     'Afgerond',
  OVERGESLAGEN: 'Overgeslagen',
  VERLOPEN:     'Verlopen',
}

export const CONTRACT_MIJLPAAL_DOELGROEP_LABELS: Record<ContractMijlpaalDoelgroep, string> = {
  HR:          'HR',
  TEAMLEIDER:  'Teamleider',
  DIRECTIE:    'Directie',
  FACILITIES:  'Facilities',
  RECRUITMENT: 'Recruitment',
}

export const CONTRACT_MIJLPAAL_WERKDAG_CORRECTIE_LABELS: Record<
  ContractMijlpaalWerkdagCorrectie,
  string
> = {
  GEEN:             'Niet verschuiven',
  VORIGE_WERKDAG:   'Naar de vorige werkdag',
  VOLGENDE_WERKDAG: 'Naar de volgende werkdag',
}

export const CONTRACT_MIJLPAAL_OVERSLAAN_REDEN_LABELS: Record<ContractMijlpaalOverslaanReden, string> = {
  ONBEPAALDE_TIJD:  'Contract voor onbepaalde tijd: er is geen contracteinde',
  GEEN_PROEFTIJD:   'Geen proeftijd van toepassing',
  VERLENGING:        'Verlengingscontract: de startreeks is niet van toepassing',
  NIET_ONDERTEKEND:  'Contract is nog niet ondertekend',
  NIET_IN_SJABLOON:  'Uitgezet in het sjabloon',
  MOMENT_VERSTREKEN: 'Het moment lag al in het verleden toen de reeks startte',
  MIGRATIE:          'Migratie: het moment lag al in het verleden',
  BEEINDIGD:         'Contract beëindigd: de reeks is gestopt',
  HANDMATIG:         'Handmatig overgeslagen',
}

export const CONTRACT_MIJLPAAL_FASE_LABELS: Record<ContractMijlpaalFase, string> = {
  START:      'Start',
  PROEFTIJD:  'Proeftijd',
  VERLENGING: 'Verlenging',
}

export const CONTRACT_PROEFTIJD_UITKOMST_LABELS: Record<ContractProeftijdUitkomst, string> = {
  VOORTZETTEN:               'Voortzetten',
  BEEINDIGEN:                'Beëindigen in de proeftijd',
  VERLENGEN_AANDACHTSPUNTEN: 'Voortzetten met aandachtspunten',
}

export const CONTRACT_MIJLPAAL_AUDIT_ACTIE_LABELS: Record<ContractMijlpaalAuditActie, string> = {
  AANGEMAAKT:           'Aangemaakt',
  GEOPEND:              'Geopend',
  AFGEROND:             'Afgerond',
  OVERGESLAGEN:         'Overgeslagen',
  VERLOPEN:             'Verlopen',
  GEESCALEERD:          'Geëscaleerd',
  VERSCHOVEN:           'Verschoven',
  HEROPEND:             'Heropend',
  HERSTELD:             'Weer van toepassing',
  CORRECTIE_VERZONDEN:  'Correctiemelding verzonden',
  GESPREK_GEKOPPELD:    'Gesprek gekoppeld',
  FORMULIER_OPGESLAGEN: 'Formulier opgeslagen',
  CHECKLIST_GEWIJZIGD:  'Checklist gewijzigd',
  TEAMLEIDER_GEWIJZIGD: 'Teamleider gewijzigd',
  REEKS_GESTOPT:        'Reeks gestopt',
  REEKS_HERVAT:         'Reeks hervat',
  OFFBOARDING_GESTART:  'Offboarding gestart',
}

// ── Koppeling met de bestaande modules ───────────────────────────────────────

/** `OperationeleTaak.bronModule` van elke mijlpaal-taak (spec §2.C.4). */
export const CONTRACT_MIJLPAAL_BRON_MODULE = 'CONTRACT'
/** `OperationeleTaak.bronSubKey` van de HR-taak "Teamleider toewijzen" (spec §5.1). */
export const CONTRACT_MIJLPAAL_TL_TAAK_SUBKEY = 'TL_TOEWIJZEN'
/** `NotificatieEvent.module` (= `InboxItem.bronModule`) van alle mijlpaal-events. */
export const CONTRACT_MIJLPAAL_NOTIFICATIE_MODULE = 'contract-mijlpalen'
/** `OperationeleTaak.categorie` van de mijlpaal-taken (zoals 'Onboarding' bij FO-003). */
export const CONTRACT_MIJLPAAL_TAAK_CATEGORIE = 'Contract'

/** Gesprekstypen (keys van `GesprekTypeConfig`) die de reeks gebruikt. */
export const CONTRACT_GESPREK_TYPE_KEYS = {
  START:            'STARTGESPREK',
  EINDE_PROEFTIJD:  'EINDE_PROEFTIJD',
  EVALUATIE:        'EVALUATIE',
  EVALUATIE_INTERN: 'EVALUATIE_INTERN',
  CONTRACTGESPREK:  'CONTRACTGESPREK',
  // batch-71 kennismaking (FO-534): zie KENNISMAKING_GESPREK_TYPE_KEY.
  KENNISMAKING:     'KENNISMAKING_VOOR_START',
} as const

/**
 * Het notificatie-event per mijlpaal. Eén key per mijlpaal zodat de ontvangers
 * per mijlpaal instelbaar zijn (FO-416) en elk event zijn eigen postvak-gedrag
 * houdt. De standaard-doelgroep komt uit het sjabloon (resolver
 * `contract-mijlpaal.doelgroep`).
 */
export const CONTRACT_MIJLPAAL_EVENT_KEYS = {
  M1: 'CONTRACT_MIJLPAAL_KENNISMAKING',
  M2: 'CONTRACT_MIJLPAAL_START_NADERT',
  // batch-71 kennismaking (FO-534): naar de verantwoordelijke en HR.
  M2B: 'KENNISMAKING_GEPLAND',
  M3: 'CONTRACT_MIJLPAAL_FACILITIES',
  M4: 'CONTRACT_MIJLPAAL_SUCCES_WENSEN',
  M5: 'CONTRACT_MIJLPAAL_CHECKIN_WEEK1',
  M6: 'CONTRACT_MIJLPAAL_CHECKIN_MAAND1',
  M7: 'CONTRACT_MIJLPAAL_OVERLEG_PROEFTIJD',
  M8: 'CONTRACT_MIJLPAAL_PROEFTIJD_EINDE',
  M9: 'CONTRACT_MIJLPAAL_VERLENGING',
} as const satisfies Record<ContractMijlpaalCode, NotificatieEventTypeKey>

/** De overige events van de reeks (escalatie, correctie, teamleider ontbreekt). */
export const CONTRACT_MIJLPAAL_ESCALATIE_EVENT = 'CONTRACT_MIJLPAAL_ESCALATIE' satisfies NotificatieEventTypeKey
/** batch-71 kennismaking (FO-534 §7): de escalatie van M2B heeft een eigen melding. */
export const KENNISMAKING_ESCALATIE_EVENT = 'KENNISMAKING_DEADLINE_VERLOPEN' satisfies NotificatieEventTypeKey
export const CONTRACT_MIJLPAAL_CORRECTIE_EVENT = 'CONTRACT_MIJLPAAL_CORRECTIE' satisfies NotificatieEventTypeKey
export const CONTRACT_MIJLPAAL_TL_TOEWIJZEN_EVENT = 'CONTRACT_MIJLPAAL_TL_TOEWIJZEN' satisfies NotificatieEventTypeKey

/** Alle event-keys van deze module (reconciliatie + sluiten van postvak-items). */
export const CONTRACT_MIJLPAAL_ALLE_EVENT_KEYS: readonly NotificatieEventTypeKey[] = [
  ...Object.values(CONTRACT_MIJLPAAL_EVENT_KEYS),
  CONTRACT_MIJLPAAL_ESCALATIE_EVENT,
  CONTRACT_MIJLPAAL_CORRECTIE_EVENT,
  CONTRACT_MIJLPAAL_TL_TOEWIJZEN_EVENT,
  KENNISMAKING_ESCALATIE_EVENT,
]

// ── Proeftijdregel (spec §2.A.1 + aanname A1) ────────────────────────────────

/**
 * Afleiding van de proeftijd uit de contractduur. Als constanten hier zodat de
 * regel op een plek staat en HR hem (per beslispunt §11 A1) kan laten bijstellen:
 * tot en met `geenProeftijdTotMaanden` geen proeftijd; tot en met
 * `korteProeftijdTotMaanden` een proeftijd van `korteProeftijdMaanden`; daarboven
 * en bij onbepaalde tijd `langeProeftijdMaanden`.
 */
export interface ProeftijdRegel {
  geenProeftijdTotMaanden: number
  korteProeftijdTotMaanden: number
  korteProeftijdMaanden: number
  langeProeftijdMaanden: number
}

export const PROEFTIJD_REGEL: ProeftijdRegel = {
  geenProeftijdTotMaanden: 6,
  korteProeftijdTotMaanden: 24,
  korteProeftijdMaanden: 1,
  langeProeftijdMaanden: 2,
}

/** Het einde-proeftijdgesprek ligt uiterlijk zoveel werkdagen voor het einde van de proeftijd. */
export const PROEFTIJD_GESPREK_WERKDAGEN_VOORAF = 3

/**
 * Werk waarvan de deadline bij de start van de reeks al voorbij is, maar korter
 * dan dit aantal dagen, gaat toch open (en escaleert dan direct). Zo mist een
 * contract dat pas op de startdag wordt getekend de facilities-check niet stil.
 * Langer geleden wordt overgeslagen.
 */
export const CONTRACT_MIJLPAAL_INHAAL_DAGEN = 14

// ── Sjabloon: de standaardreeks M1 t/m M9 (spec §2.B) ────────────────────────

/** Eén regel van een mijlpaal-sjabloon (ook de vorm van de geseede standaardreeks). */
export interface ContractMijlpaalSjabloonRegelBasis {
  code: ContractMijlpaalCode
  actief: boolean
  anker: ContractMijlpaalAnker
  /** Negatief = voor het anker, positief = erna. */
  offsetAantal: number
  offsetEenheid: ContractMijlpaalOffsetEenheid
  werkdagCorrectie: ContractMijlpaalWerkdagCorrectie
  /** 'HH:mm' (NL-tijd) wanneer het moment een tijdstip heeft (M4: 07:00). */
  tijdstip: string | null
  /** Aantal kalenderdagen voor het moment waarop de mijlpaal al opengaat. */
  voorloopDagen: number
  /** Deadline als eigen anker plus offset; wint van `termijnDagen`. */
  deadlineAnker: ContractMijlpaalAnker | null
  deadlineOffsetAantal: number
  deadlineOffsetEenheid: ContractMijlpaalOffsetEenheid
  /** Deadline als aantal kalenderdagen na het moment (null = geen termijn). */
  termijnDagen: number | null
  doelgroepen: ContractMijlpaalDoelgroep[]
  /** Wie een melding krijgt als de mijlpaal verloopt (leeg = geen escalatiemelding). */
  escalatieDoelgroepen: ContractMijlpaalDoelgroep[]
  actie: ContractMijlpaalActie
  titel: string
  omschrijving: string
  checklist: string[]
  /** Key van het gesprekstype dat bij deze mijlpaal gepland wordt (null = geen). */
  gesprekTypeKey: string | null
}

export const CONTRACT_MIJLPAAL_STANDAARD_SJABLOON_NAAM = 'Standaardreeks'

/**
 * De geseede reeks, exact zoals de tabel in spec 196 §2.B. Dit is de ENIGE bron:
 * de seed, de migratie-spiegel en de terugval van de motor (geen sjabloon in de
 * DB gevonden) lezen deze lijst. Zichtbare teksten zonder em-dash.
 */
export const CONTRACT_MIJLPAAL_STANDAARD_REEKS: readonly ContractMijlpaalSjabloonRegelBasis[] = [
  {
    code: 'M1',
    actief: true,
    anker: 'ONDERTEKEND',
    offsetAantal: 0,
    offsetEenheid: 'DAGEN',
    werkdagCorrectie: 'GEEN',
    tijdstip: null,
    voorloopDagen: 0,
    deadlineAnker: 'START',
    deadlineOffsetAantal: -1,
    deadlineOffsetEenheid: 'DAGEN',
    termijnDagen: null,
    doelgroepen: ['TEAMLEIDER', 'HR'],
    escalatieDoelgroepen: ['HR'],
    actie: 'TAAK',
    titel: 'Kennismaking en planning doornemen inplannen',
    omschrijving:
      'Plan de kennismaking en neem de planning door. Plan het startgesprek voor de startdatum.',
    checklist: ['Kennismaking', 'Planning en rooster', 'Werkplek of klant', 'Buddy'],
    gesprekTypeKey: 'STARTGESPREK',
  },
  {
    code: 'M2',
    actief: true,
    anker: 'START',
    offsetAantal: -2,
    offsetEenheid: 'WEKEN',
    werkdagCorrectie: 'VORIGE_WERKDAG',
    tijdstip: null,
    voorloopDagen: 0,
    // Een melding heeft geen afrondtermijn; deze deadline is het venster waarin
    // hij nog zin heeft (een laat getekend contract krijgt hem alsnog, tot de
    // dag voor de start).
    deadlineAnker: 'START',
    deadlineOffsetAantal: -1,
    deadlineOffsetEenheid: 'DAGEN',
    termijnDagen: null,
    doelgroepen: ['HR', 'TEAMLEIDER', 'DIRECTIE'],
    escalatieDoelgroepen: [],
    actie: 'MELDING',
    titel: 'Start nieuwe medewerker',
    omschrijving: 'Melding met de contractsamenvatting, twee weken voor de startdatum.',
    checklist: [],
    gesprekTypeKey: null,
  },
  // batch-71 kennismaking (FO-534 §A1 + A5): 2 weken = 10 werkdagen, deadline 5
  // werkdagen voor de start. De regel plant gesprek, taak, blok en uitnodiging; zie
  // `apps/api/src/services/kennismaking/motor.ts`. Bij een korte aanloop wordt de
  // deadline START -1 werkdag (`kennismakingPlanBijOpening` in @streep/utils).
  {
    code: 'M2B',
    actief: true,
    anker: 'START',
    offsetAantal: -10,
    offsetEenheid: 'WERKDAGEN',
    werkdagCorrectie: 'GEEN',
    tijdstip: null,
    voorloopDagen: 0,
    deadlineAnker: 'START',
    deadlineOffsetAantal: -5,
    deadlineOffsetEenheid: 'WERKDAGEN',
    termijnDagen: null,
    doelgroepen: ['HR', 'TEAMLEIDER'],
    escalatieDoelgroepen: ['HR'],
    actie: 'GESPREK_PLANNEN',
    titel: 'Kennismaking voor de start',
    omschrijving:
      'Neem contact op met de nieuwe medewerker en loop de nalooplijst door. Rond de lijst af: dan ontstaat het verslag met de actiepunten.',
    checklist: [],
    gesprekTypeKey: 'KENNISMAKING_VOOR_START',
  },
  {
    code: 'M3',
    actief: true,
    anker: 'START',
    offsetAantal: -1,
    offsetEenheid: 'WEKEN',
    werkdagCorrectie: 'VORIGE_WERKDAG',
    tijdstip: null,
    voorloopDagen: 0,
    deadlineAnker: 'START',
    deadlineOffsetAantal: -1,
    deadlineOffsetEenheid: 'DAGEN',
    termijnDagen: null,
    doelgroepen: ['FACILITIES'],
    escalatieDoelgroepen: ['HR'],
    actie: 'TAAK',
    titel: 'Middelen klaarzetten voor de start',
    omschrijving:
      'Vink per onderdeel af wat klaarstaat. Op de startdag moet alles afgevinkt zijn, anders gaat er een melding naar HR.',
    checklist: [
      'Kleding en PBM',
      'Gereedschap',
      'Voertuig',
      'Telefoon en SIM',
      'Accounts en toegang',
      'Certificaten',
    ],
    gesprekTypeKey: null,
  },
  {
    code: 'M4',
    actief: true,
    anker: 'START',
    offsetAantal: 0,
    offsetEenheid: 'DAGEN',
    werkdagCorrectie: 'GEEN',
    tijdstip: '07:00',
    voorloopDagen: 0,
    deadlineAnker: null,
    deadlineOffsetAantal: 0,
    deadlineOffsetEenheid: 'DAGEN',
    termijnDagen: 0,
    doelgroepen: ['HR'],
    escalatieDoelgroepen: [],
    actie: 'TAAK',
    titel: 'Succes wensen',
    omschrijving: 'Bel of stuur een bericht op de eerste werkdag en vink de taak af.',
    checklist: [],
    gesprekTypeKey: null,
  },
  {
    code: 'M5',
    actief: true,
    anker: 'EINDE_WEEK_1',
    offsetAantal: 0,
    offsetEenheid: 'DAGEN',
    werkdagCorrectie: 'VORIGE_WERKDAG',
    tijdstip: null,
    voorloopDagen: 0,
    deadlineAnker: null,
    deadlineOffsetAantal: 0,
    deadlineOffsetEenheid: 'DAGEN',
    termijnDagen: 7,
    doelgroepen: ['TEAMLEIDER'],
    escalatieDoelgroepen: ['HR'],
    actie: 'TAAK',
    titel: 'Check-in medewerker en klant',
    omschrijving:
      'Vul het korte formulier in en plan het einde-proeftijdgesprek. Zonder gepland gesprek kan de taak niet worden afgerond.',
    checklist: [],
    gesprekTypeKey: 'EINDE_PROEFTIJD',
  },
  {
    code: 'M6',
    actief: true,
    anker: 'EINDE_MAAND_1',
    offsetAantal: 0,
    offsetEenheid: 'DAGEN',
    werkdagCorrectie: 'VORIGE_WERKDAG',
    tijdstip: null,
    voorloopDagen: 0,
    deadlineAnker: null,
    deadlineOffsetAantal: 0,
    deadlineOffsetEenheid: 'DAGEN',
    termijnDagen: 7,
    doelgroepen: ['TEAMLEIDER'],
    escalatieDoelgroepen: ['HR'],
    actie: 'TAAK',
    titel: 'Telefonische check-in',
    omschrijving: 'Bel de medewerker en vul het korte formulier in.',
    checklist: [],
    gesprekTypeKey: null,
  },
  {
    code: 'M7',
    actief: true,
    anker: 'PROEFTIJD_EINDE',
    offsetAantal: -2,
    offsetEenheid: 'WEKEN',
    werkdagCorrectie: 'VORIGE_WERKDAG',
    tijdstip: null,
    voorloopDagen: 0,
    deadlineAnker: null,
    deadlineOffsetAantal: 0,
    deadlineOffsetEenheid: 'DAGEN',
    termijnDagen: 7,
    doelgroepen: ['HR', 'TEAMLEIDER'],
    escalatieDoelgroepen: ['HR'],
    actie: 'TAAK',
    titel: 'Overleg proeftijd',
    omschrijving:
      'Leg de kant van HR (verzuim, administratie, signalen) en van de teamleider (technisch, klantfeedback) vast en kies een voorstel.',
    checklist: [],
    gesprekTypeKey: null,
  },
  {
    code: 'M8',
    actief: true,
    anker: 'PROEFTIJD_EINDE',
    offsetAantal: 0,
    offsetEenheid: 'DAGEN',
    werkdagCorrectie: 'GEEN',
    tijdstip: null,
    voorloopDagen: 7,
    deadlineAnker: 'PROEFTIJD_EINDE',
    deadlineOffsetAantal: -1,
    deadlineOffsetEenheid: 'DAGEN',
    termijnDagen: null,
    doelgroepen: ['TEAMLEIDER', 'HR'],
    escalatieDoelgroepen: ['HR', 'DIRECTIE'],
    actie: 'VERSLAG_VERPLICHT',
    titel: 'Einde proeftijd: gesprek en verslag',
    omschrijving:
      'Het einde-proeftijdgesprek is gevoerd en het schriftelijke verslag is door de medewerker ondertekend voor de einddatum. Leg de uitkomst vast.',
    checklist: [],
    gesprekTypeKey: 'EINDE_PROEFTIJD',
  },
  {
    code: 'M9',
    actief: true,
    anker: 'CONTRACT_EINDE',
    offsetAantal: -2,
    offsetEenheid: 'MAANDEN',
    werkdagCorrectie: 'VORIGE_WERKDAG',
    tijdstip: null,
    voorloopDagen: 0,
    // Besluit en aanzegging uiterlijk een maand voor het einde (spec §2.B, M9).
    deadlineAnker: 'CONTRACT_EINDE',
    deadlineOffsetAantal: -1,
    deadlineOffsetEenheid: 'MAANDEN',
    termijnDagen: null,
    doelgroepen: ['HR', 'TEAMLEIDER', 'DIRECTIE'],
    // De aanzegbewaking (FO-388) escaleert zelf op T-30; geen tweede signaal.
    escalatieDoelgroepen: [],
    actie: 'GESPREK_PLANNEN',
    titel: 'Besluit verlenging',
    omschrijving:
      'Plan eerst het interne besluitgesprek en leg het besluit vast. Plan daarna het gesprek met de medewerker.',
    checklist: [],
    gesprekTypeKey: 'EVALUATIE_INTERN',
  },
]

/**
 * De actie van een mijlpaal ligt vast per code. De harde regels (checklist bij
 * M3, gepland gesprek bij M5, verslag bij M8, besluit bij M9) hangen aan de
 * code; zou een sjabloon van zo'n mijlpaal een melding maken, dan rondt hij
 * zichzelf af en is de regel omzeild. Een mijlpaal die niet past zet je uit.
 */
export const CONTRACT_MIJLPAAL_ACTIE_PER_CODE: Record<ContractMijlpaalCode, ContractMijlpaalActie> =
  Object.fromEntries(CONTRACT_MIJLPAAL_STANDAARD_REEKS.map((r) => [r.code, r.actie])) as Record<
    ContractMijlpaalCode,
    ContractMijlpaalActie
  >

/**
 * Mijlpalen waarvan de afrondregel een gesprek eist: zonder gesprekstype valt er
 * niets te plannen en zou de mijlpaal nooit af te ronden zijn.
 */
export const CONTRACT_MIJLPAAL_GESPREK_VERPLICHT: readonly ContractMijlpaalCode[] = ['M1', 'M2B', 'M5', 'M8', 'M9']

/**
 * Escalatiegroepen die een sjabloon niet mag weghalen. De facilities-checklist
 * escaleert naar HR als hij op de startdag niet af is; het proeftijdverslag naar
 * HR en de directie als het op de einddatum ontbreekt. Zonder deadline of zonder
 * deze groepen zou de regel stil vervallen.
 */
export const CONTRACT_MIJLPAAL_VASTE_ESCALATIE: Partial<
  Record<ContractMijlpaalCode, readonly ContractMijlpaalDoelgroep[]>
> = {
  // batch-71 kennismaking (FO-534 §A5): zonder gesprek voor de deadline escaleert het naar HR.
  M2B: ['HR'],
  M3: ['HR'],
  M8: ['HR', 'DIRECTIE'],
}

/**
 * De onderdelen van de facilities-checklist (M3) die het systeem zelf kan
 * afvinken: de start-checklist van de onboarding (FO-003) weet al of het middel
 * is toegewezen. De koppeling loopt via een woord in de tekst van het onderdeel,
 * zodat HR de tekst mag aanpassen zolang dat woord erin blijft. Een onderdeel
 * zonder zo'n woord (accounts, toegang) blijft handwerk.
 */
export const CONTRACT_MIJLPAAL_STARTCHECKLIST_WOORDEN = {
  KLEDING:      'kleding',
  GEREEDSCHAP:  'gereedschap',
  VOERTUIG:     'voertuig',
  TELEFOON_SIM: 'telefoon',
  CERTIFICATEN: 'certifica',
} as const
export type ContractMijlpaalStartchecklistStap = keyof typeof CONTRACT_MIJLPAAL_STARTCHECKLIST_WOORDEN

/** Welke stap van de start-checklist hoort bij dit checklist-onderdeel (null = handwerk). */
export function startchecklistStapVoorOnderdeel(label: string): ContractMijlpaalStartchecklistStap | null {
  const tekst = label.toLowerCase()
  for (const [stap, woord] of Object.entries(CONTRACT_MIJLPAAL_STARTCHECKLIST_WOORDEN)) {
    if (tekst.includes(woord)) return stap as ContractMijlpaalStartchecklistStap
  }
  return null
}

// ── Formulieren (formulierData op de mijlpaal) ───────────────────────────────

const tekstVerplicht = (label: string, max = 2000) =>
  z
    .string({ message: `${label} is verplicht.` })
    .trim()
    .min(1, `${label} is verplicht.`)
    .max(max, `${label} is te lang (maximaal ${max} tekens).`)

const tekstOptioneel = (label: string, max = 2000) =>
  z
    .string()
    .trim()
    .max(max, `${label} is te lang (maximaal ${max} tekens).`)
    .nullable()
    .optional()

/** Kort check-in-formulier (M5 week 1, M6 maand 1): hoe gaat het, klantfeedback, actiepunten. */
export const contractCheckinFormulierSchema = z.object({
  hoeGaatHet: tekstVerplicht('Hoe gaat het'),
  klantFeedback: tekstOptioneel('Klantfeedback'),
  actiepunten: tekstOptioneel('Actiepunten'),
})
export type ContractCheckinFormulier = z.infer<typeof contractCheckinFormulierSchema>

/**
 * Overleg proeftijd (M7): de kant van HR en de kant van de teamleider, plus een
 * voorstel. Beide kanten zijn verplicht bij afronden; tussentijds opslaan mag
 * met een kant leeg (zie `contractOverlegFormulierConceptSchema`).
 */
export const contractOverlegFormulierSchema = z.object({
  hrVerzuim: tekstVerplicht('Verzuim'),
  hrAdministratie: tekstVerplicht('Administratie'),
  hrSignalen: tekstOptioneel('Signalen'),
  tlTechnisch: tekstVerplicht('Technisch functioneren'),
  tlKlantFeedback: tekstVerplicht('Klantfeedback'),
  voorstel: z.enum(CONTRACT_PROEFTIJD_UITKOMSTEN, { message: 'Kies een voorstel.' }),
  toelichting: tekstOptioneel('Toelichting'),
})
export type ContractOverlegFormulier = z.infer<typeof contractOverlegFormulierSchema>

/** Tussentijds opslaan van een formulier: alle velden optioneel, zelfde limieten. */
export const contractCheckinFormulierConceptSchema = z.object({
  hoeGaatHet: tekstOptioneel('Hoe gaat het'),
  klantFeedback: tekstOptioneel('Klantfeedback'),
  actiepunten: tekstOptioneel('Actiepunten'),
})
export const contractOverlegFormulierConceptSchema = z.object({
  hrVerzuim: tekstOptioneel('Verzuim'),
  hrAdministratie: tekstOptioneel('Administratie'),
  hrSignalen: tekstOptioneel('Signalen'),
  tlTechnisch: tekstOptioneel('Technisch functioneren'),
  tlKlantFeedback: tekstOptioneel('Klantfeedback'),
  voorstel: z.enum(CONTRACT_PROEFTIJD_UITKOMSTEN).nullable().optional(),
  toelichting: tekstOptioneel('Toelichting'),
})

/** Labels voor de inline veldfouten (`summarizeValidationIssues`). */
export const CONTRACT_MIJLPAAL_FORMULIER_VELD_LABELS: Record<string, string> = {
  hoeGaatHet:      'Hoe gaat het',
  klantFeedback:   'Klantfeedback',
  actiepunten:     'Actiepunten',
  hrVerzuim:       'Verzuim',
  hrAdministratie: 'Administratie',
  hrSignalen:      'Signalen',
  tlTechnisch:     'Technisch functioneren',
  tlKlantFeedback: 'Klantfeedback',
  voorstel:        'Voorstel',
  toelichting:     'Toelichting',
  uitkomst:        'Uitkomst',
  reden:           'Reden',
  datum:           'Datum',
  tijd:            'Tijd',
  gesprekspartnerEmployeeId: 'Gespreksvoerder',
  proeftijdEindDatum: 'Einde proeftijd',
  teamleiderEmployeeId: 'Teamleider',
  klantContextClientId: 'Eerste inzetklant',
  startDatum:      'Startdatum',
  eindDatum:       'Einddatum',
}

// ── Afrond-controle (spec §5.6: weigeren met inline uitleg) ──────────────────

/** Wat er ontbreekt om een mijlpaal af te ronden. `veld` stuurt de inline melding. */
export const CONTRACT_MIJLPAAL_BLOKKADE_CODES = [
  'CHECKLIST_ONVOLLEDIG',
  'FORMULIER_ONTBREEKT',
  'GESPREK_NIET_GEPLAND',
  'GESPREK_TE_LAAT',
  'VERSLAG_ONTBREEKT',
  'VERSLAG_NIET_ONDERTEKEND',
  'UITKOMST_ONTBREEKT',
  'BESLUIT_ONTBREEKT',
  'VERVOLGGESPREK_NIET_GEPLAND',
] as const
export type ContractMijlpaalBlokkadeCode = (typeof CONTRACT_MIJLPAAL_BLOKKADE_CODES)[number]

export interface ContractMijlpaalBlokkade {
  code: ContractMijlpaalBlokkadeCode
  /** Onderdeel van de kaart waar de melding bij hoort. */
  veld: 'checklist' | 'formulier' | 'gesprek' | 'verslag' | 'uitkomst' | 'besluit'
  melding: string
}

// ── DTO's ────────────────────────────────────────────────────────────────────

export interface ContractMijlpaalChecklistItemDTO {
  id: string
  label: string
  gedaan: boolean
  gedaanOp: string | null
}

/** Het gesprek dat aan een mijlpaal hangt (geen notities: die blijven in de gespreksagenda). */
export interface ContractMijlpaalGesprekDTO {
  id: string
  typeKey: string
  typeLabel: string
  /** UTC-instant (ISO); de UI toont hem in Europe/Amsterdam. */
  geplandOp: string
  status: 'GEPLAND' | 'VERZET' | 'AFGEROND' | 'GEANNULEERD'
  gesprekspartnerNaam: string | null
  verslagVerplicht: boolean
  /** Het verslag is vrijgegeven aan de medewerker. */
  verslagVrijgegeven: boolean
  /**
   * Het verslagtraject is rond: de medewerker heeft getekend, het stilzwijgend
   * akkoord is ingegaan, de weigering om te tekenen is vastgelegd of het verslag
   * is administratief afgesloten.
   */
  verslagAkkoord: boolean
  /** De medewerker heeft geweigerd te tekenen (vastgelegd in het verslag). */
  verslagGeweigerd: boolean
  verslagId: string | null
}

export interface ContractMijlpaalDTO {
  id: string
  contractId: string
  code: ContractMijlpaalCode
  fase: ContractMijlpaalFase
  faseLabel: string
  titel: string
  omschrijving: string
  actie: ContractMijlpaalActie
  actieLabel: string
  status: ContractMijlpaalStatus
  statusLabel: string
  variant: ContractMijlpaalVariant
  anker: ContractMijlpaalAnker
  ankerLabel: string
  /** Het moment van de mijlpaal (yyyy-mm-dd); null zolang hij is overgeslagen zonder datum. */
  momentOp: string | null
  tijdstip: string | null
  /** Vanaf wanneer de mijlpaal open staat (moment minus de voorlooptijd). */
  opentOp: string | null
  deadlineOp: string | null
  /** Dagen tot de deadline (negatief = te laat); null zonder deadline. */
  dagenTotDeadline: number | null
  doelgroepen: ContractMijlpaalDoelgroep[]
  doelgroepLabels: string[]
  overslaanReden: ContractMijlpaalOverslaanReden | null
  overslaanRedenLabel: string | null
  overslaanToelichting: string | null
  geopendOp: string | null
  afgerondOp: string | null
  afgerondDoorNaam: string | null
  verlopenOp: string | null
  geescaleerdOp: string | null
  taakId: string | null
  checklist: ContractMijlpaalChecklistItemDTO[]
  formulierSoort: ContractMijlpaalFormulierSoort | null
  formulierData: Record<string, unknown> | null
  /** Het gesprekstype dat bij deze mijlpaal gepland moet worden (null = geen). */
  gesprekTypeKey: string | null
  gesprekTypeLabel: string | null
  /** Uiterste datum voor dat gesprek (M5: einde proeftijd minus 3 werkdagen). */
  gesprekUiterlijkOp: string | null
  gesprek: ContractMijlpaalGesprekDTO | null
  /** M9: het gesprek met de medewerker, na het interne besluitgesprek. */
  vervolgGesprek: ContractMijlpaalGesprekDTO | null
  /**
   * Gesprekken van het gevraagde type die al in de gespreksagenda staan maar nog
   * niet aan deze mijlpaal hangen. Leeg zodra er een gesprek gekoppeld is, zodat
   * de kaart alleen dan "dit gesprek koppelen" aanbiedt.
   */
  koppelbareGesprekken: ContractMijlpaalGesprekDTO[]
  /** M9: idem voor het gesprek met de medewerker (pas na het vastgelegde besluit). */
  koppelbareVervolgGesprekken: ContractMijlpaalGesprekDTO[]
  uitkomst: ContractProeftijdUitkomst | null
  uitkomstLabel: string | null
  /** M9: het vastgelegde verlengingsbesluit (FO-388), of null. */
  besluit: { besluit: ContractVerlengingBesluitType; besluitLabel: string; besluitOp: string } | null
  /** Wat er nog ontbreekt om af te ronden (leeg = afronden kan). */
  blokkades: ContractMijlpaalBlokkade[]
  /** De ingelogde gebruiker mag deze mijlpaal behandelen. */
  magBehandelen: boolean
}

export interface ContractKetenRegelDTO {
  contractId: string
  typeLabel: string
  statusLabel: string
  startDatum: string
  eindDatum: string | null
  /** Dit is het contract van het dossier dat nu open staat. */
  huidig: boolean
}

export interface ContractMijlpaalAuditDTO {
  id: string
  mijlpaalCode: ContractMijlpaalCode | null
  actie: ContractMijlpaalAuditActie
  actieLabel: string
  actorNaam: string | null
  toelichting: string | null
  createdAt: string
}

/** Het contractdossier: de workflow-gegevens, de keten en de tijdlijn (spec §2.D.6). */
export interface ContractDossierDTO {
  contractId: string
  employeeId: string
  medewerkerNaam: string
  functie: string | null
  entiteitNaam: string | null
  contractTypeLabel: string
  contractDuurLabel: string | null
  contractStatusLabel: string
  startDatum: string
  eindDatum: string | null
  /** Dagen tot de einddatum (negatief = verlopen); null bij onbepaalde tijd. */
  dagenTotEinde: number | null
  ondertekendOp: string | null
  proeftijdEindDatum: string | null
  /** De proeftijd zoals de regel hem zou afleiden (voor "terug naar afgeleid"). */
  proeftijdAfgeleid: string | null
  /** HR heeft de proeftijd zelf gezet (of bewust weggehaald); hij volgt de datums dan niet meer. */
  proeftijdHandmatig: boolean
  /** Dagen tot het einde van de proeftijd (negatief = voorbij); null zonder proeftijd. */
  dagenTotProeftijdEinde: number | null
  teamleiderEmployeeId: string | null
  teamleiderNaam: string | null
  /** De teamleider komt uit de afdeling (true) of is op het contract ingevuld (false). */
  teamleiderUitAfdeling: boolean
  klantContextClientId: string | null
  klantContextNaam: string | null
  isVerlenging: boolean
  /** De mijlpaalreeks van dit contract is gestart (bij ondertekenen of activeren). */
  reeksGestart: boolean
  /** De reeks is gestopt (contract beeindigd, ingetrokken of verlopen). */
  reeksGestopt: boolean
  /** Waarom de reeks is gestopt, in gewone taal (null zolang hij loopt). */
  reeksGestoptReden: string | null
  /** HR kan een gestopte reeks hervatten (het contract loopt nog en er staat geen datum uit dienst). */
  kanReeksHervatten: boolean
  /** De datum uit dienst op het medewerkerprofiel, als die bij dit contract hoort (op of na de start). */
  datumUitDienst: string | null
  /** De proeftijd loopt nog en HR kan het dienstverband in de proeftijd beeindigen. */
  kanBeeindigenInProeftijd: boolean
  /**
   * De startdatum op het medewerkerprofiel, als die afwijkt van het contract. De
   * reeks rekent met het contract; null = gelijk of niet ingevuld.
   */
  afwijkendeStartDatumMedewerker: string | null
  /**
   * HR kan de contractdatums en de proeftijd hier corrigeren: alleen bij een
   * ondertekend of actief contract (een concept wijzig je in het contract zelf).
   */
  kanDatumsCorrigeren: boolean
  /**
   * Het contract loopt, maar heeft nog geen reeks (getekend voordat de workflow
   * bestond): HR kan hem alsnog starten, vanaf nu.
   */
  kanReeksStarten: boolean
  sjabloonNaam: string | null
  keten: ContractKetenRegelDTO[]
  mijlpalen: ContractMijlpaalDTO[]
  audit: ContractMijlpaalAuditDTO[]
  /** De ingelogde gebruiker mag de workflow-gegevens en de reeks beheren (HR/ADMIN). */
  magBeheren: boolean
  /** De ingelogde gebruiker als medewerker (voorinvulling van de gespreksvoerder). */
  actorEmployeeId: string | null
}

/**
 * Eén mijlpaal met net genoeg context om hem te behandelen. Wie alleen in de
 * doelgroep van de mijlpaal zit (bijvoorbeeld facilities) ziet de naam, de
 * functie en de startdatum, niet de proeftijd of het contracteinde: die staan
 * alleen in het dossier (`magDossierZien`).
 */
export interface ContractMijlpaalDetailDTO {
  mijlpaal: ContractMijlpaalDTO
  contractId: string
  employeeId: string
  medewerkerNaam: string
  functie: string | null
  startDatum: string
  klantContextNaam: string | null
  teamleiderNaam: string | null
  /** De teamleider die op het contract is ingevuld (null = uit de afdeling of geen). */
  teamleiderEmployeeId: string | null
  magDossierZien: boolean
  /**
   * HR of beheerder met schrijfrecht op medewerkers: mag overslaan, heropenen en
   * bij M8 de uitkomst "beëindigen" vastleggen.
   */
  magBeheren: boolean
  /** De ingelogde gebruiker als medewerker (voorinvulling van de gespreksvoerder). */
  actorEmployeeId: string | null
}

/** Rij van het HR-overzicht "Contractbewaking" (spec §2.D.7). */
export interface ContractbewakingRijDTO {
  mijlpaalId: string
  contractId: string
  employeeId: string
  medewerkerNaam: string
  entiteitNaam: string | null
  teamleiderNaam: string | null
  code: ContractMijlpaalCode
  titel: string
  fase: ContractMijlpaalFase
  faseLabel: string
  status: ContractMijlpaalStatus
  statusLabel: string
  momentOp: string | null
  deadlineOp: string | null
  dagenTotDeadline: number | null
  doelgroepLabels: string[]
}

export interface ContractbewakingTellersDTO {
  open: number
  verlopen: number
  /** Open mijlpalen waarvan de deadline binnen zeven dagen ligt. */
  dezeWeek: number
  /** Lopende contracten zonder teamleider (afdeling noch contract). */
  zonderTeamleider: number
  perFase: Record<ContractMijlpaalFase, number>
}

export interface ContractMijlpaalSjabloonRegelDTO extends ContractMijlpaalSjabloonRegelBasis {
  id: string
  volgorde: number
}

export interface ContractMijlpaalSjabloonDTO {
  id: string
  naam: string
  omschrijving: string | null
  /** Contractvorm waarvoor dit sjabloon geldt (null = alle). */
  contractType: string | null
  contractTypeLabel: string | null
  /** BV waarvoor dit sjabloon geldt (null = alle). */
  entityId: string | null
  entiteitNaam: string | null
  actief: boolean
  isStandaard: boolean
  /** Een verlengingscontract doorloopt alleen de verlengingsreeks (M9). */
  verlengingAlleenEindreeks: boolean
  regels: ContractMijlpaalSjabloonRegelDTO[]
  updatedAt: string
}

/** Wie er in de doelgroepen Directie en Facilities zit (beheerscherm sjablonen). */
export interface ContractMijlpaalDoelgroepenDTO {
  directie: Array<{ employeeId: string; naam: string; heeftActiefAccount: boolean }>
  /** Niemand draagt de directie-vlag met een actief account: de beheerders vallen in. */
  directieValtTerugOpBeheerders: boolean
  /** Namen van wie nu in Facilities zit (uitrusting verwerken of wagenparkbeheer). */
  facilities: string[]
  hrAantal: number
}

/** Portal: een eigen gepland gesprek van de medewerker (spec §2.D.8). */
export interface MijnGesprekDTO {
  id: string
  typeLabel: string
  /** UTC-instant (ISO); de portal toont hem in Europe/Amsterdam. */
  geplandOp: string
  duurMinuten: number
  locatie: string | null
  gesprekspartnerNaam: string | null
  status: 'GEPLAND' | 'VERZET' | 'AFGEROND'
  /** Er staat een vrijgegeven verslag klaar. */
  verslagId: string | null
  /** Het verslag wacht nog op de handtekening van de medewerker. */
  verslagTeOndertekenen: boolean
}

// ── Zod-schemas (request bodies + query) ─────────────────────────────────────

const ISO_DATUM = /^\d{4}-\d{2}-\d{2}$/
const isoDatum = (label: string) => z.string().regex(ISO_DATUM, `Geef een geldige datum op voor ${label}.`)
const TIJD = /^([01]\d|2[0-3]):[0-5]\d$/
// Gesprekken worden op het kwartier gepland (zoals de gespreksagenda, FO-035).
const KWARTIER_TIJD = /^([01]\d|2[0-3]):(00|15|30|45)$/

/**
 * Workflow-gegevens van het contract. `proeftijdEindDatum: null` betekent bewust
 * "geen proeftijd"; `proeftijdAfleiden: true` zet de datum terug naar de
 * afgeleide waarde. Start- en einddatum zijn een CORRECTIE op een al getekend
 * contract (verschoven start, aangepaste einddatum) en vragen daarom een reden.
 * Die eis controleert de service, niet dit schema: alleen de service weet of een
 * meegestuurde datum ook echt afwijkt van wat er staat. Zo accepteert de PATCH
 * een echo van zijn eigen read-DTO.
 */
export const contractWorkflowGegevensSchema = z
  .object({
    proeftijdEindDatum: isoDatum('het einde van de proeftijd').nullable().optional(),
    proeftijdAfleiden: z.boolean().optional(),
    teamleiderEmployeeId: z.string().min(1).nullable().optional(),
    klantContextClientId: z.string().min(1).nullable().optional(),
    startDatum: isoDatum('de startdatum').optional(),
    eindDatum: isoDatum('de einddatum').nullable().optional(),
    reden: z.string().trim().max(500, 'De reden is te lang (maximaal 500 tekens).').nullable().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.startDatum && val.eindDatum && val.eindDatum < val.startDatum) {
      ctx.addIssue({
        code: 'custom',
        path: ['eindDatum'],
        message: 'Einddatum mag niet voor de startdatum liggen.',
      })
    }
    if (val.startDatum && val.proeftijdEindDatum && val.proeftijdEindDatum < val.startDatum) {
      ctx.addIssue({
        code: 'custom',
        path: ['proeftijdEindDatum'],
        message: 'Het einde van de proeftijd mag niet voor de startdatum liggen.',
      })
    }
  })
export type ContractWorkflowGegevensInput = z.infer<typeof contractWorkflowGegevensSchema>

/** Formulier tussentijds opslaan (de server kiest het schema op de mijlpaalcode). */
export const contractMijlpaalFormulierSchema = z.object({
  formulierData: z.record(z.string(), z.unknown()),
})
export type ContractMijlpaalFormulierInput = z.infer<typeof contractMijlpaalFormulierSchema>

export const contractMijlpaalChecklistSchema = z.object({
  gedaan: z.boolean(),
})
export type ContractMijlpaalChecklistInput = z.infer<typeof contractMijlpaalChecklistSchema>

/** Een gesprek plannen vanuit de mijlpaal (datum en tijd in NL-tijd). */
export const contractMijlpaalGesprekPlannenSchema = z.object({
  datum: isoDatum('het gesprek'),
  tijd: z.string().regex(KWARTIER_TIJD, 'Kies een tijd op het kwartier (bijvoorbeeld 10:15).'),
  gesprekspartnerEmployeeId: z
    .string({ message: 'Kies een gespreksvoerder.' })
    .min(1, 'Kies een gespreksvoerder.'),
  /**
   * Alleen bij het interne besluitgesprek (M9): wie er aan tafel zit. Bij de
   * overige gesprekken is de medewerker van het contract de deelnemer.
   */
  deelnemerEmployeeIds: z.array(z.string().min(1)).max(25).optional(),
  locatie: z.string().trim().max(200, 'De locatie is te lang (maximaal 200 tekens).').nullable().optional(),
  /** M9: true = het gesprek met de medewerker (na het interne besluitgesprek). */
  vervolg: z.boolean().optional(),
})
export type ContractMijlpaalGesprekPlannenInput = z.infer<typeof contractMijlpaalGesprekPlannenSchema>

/** Een bestaand gesprek uit de gespreksagenda aan de mijlpaal koppelen. */
export const contractMijlpaalGesprekKoppelenSchema = z.object({
  gesprekId: z.string().min(1, 'Kies een gesprek.'),
  vervolg: z.boolean().optional(),
})
export type ContractMijlpaalGesprekKoppelenInput = z.infer<typeof contractMijlpaalGesprekKoppelenSchema>

export const contractMijlpaalAfrondenSchema = z.object({
  /** M7 (voorstel uit het formulier) en M8 (uitkomst) leveren een uitkomst mee. */
  uitkomst: z.enum(CONTRACT_PROEFTIJD_UITKOMSTEN).nullable().optional(),
  /** M8 met uitkomst BEEINDIGEN: de datum waarop het dienstverband eindigt. */
  beeindigingsDatum: isoDatum('de beëindiging').nullable().optional(),
  opmerking: z.string().trim().max(2000).nullable().optional(),
})
export type ContractMijlpaalAfrondenInput = z.infer<typeof contractMijlpaalAfrondenSchema>

/**
 * Het dienstverband beeindigen in de proeftijd, als expliciete keuze van HR met
 * de laatste werkdag. Dit is iets anders dan het contractdocument beeindigen: de
 * medewerker krijgt deze einddatum en de offboarding start.
 */
export const contractBeeindigenInProeftijdSchema = z.object({
  beeindigingsDatum: isoDatum('de laatste werkdag'),
})
export type ContractBeeindigenInProeftijdInput = z.infer<typeof contractBeeindigenInProeftijdSchema>

export const contractMijlpaalOverslaanSchema = z.object({
  reden: z
    .string({ message: 'Geef een reden op.' })
    .trim()
    .min(3, 'Geef een reden op.')
    .max(500, 'De reden is te lang (maximaal 500 tekens).'),
})
export type ContractMijlpaalOverslaanInput = z.infer<typeof contractMijlpaalOverslaanSchema>

export const contractbewakingQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  /** Leeg = de werkvoorraad (open en verlopen). */
  status: z.enum(CONTRACT_MIJLPAAL_STATUSSEN).optional(),
  fase: z.enum(CONTRACT_MIJLPAAL_FASES).optional(),
  entityId: z.string().min(1).optional(),
  teamleiderEmployeeId: z.string().min(1).optional(),
  employeeId: z.string().min(1).optional(),
  zoek: z.string().trim().max(100).optional(),
})
export type ContractbewakingQuery = z.infer<typeof contractbewakingQuerySchema>

const sjabloonRegelSchema = z
  .object({
    code: z.enum(CONTRACT_MIJLPAAL_CODES),
    actief: z.boolean(),
    anker: z.enum(CONTRACT_MIJLPAAL_ANKERS),
    offsetAantal: z.number().int().min(-120, 'Maximaal 120 voor het anker.').max(120, 'Maximaal 120 na het anker.'),
    offsetEenheid: z.enum(CONTRACT_MIJLPAAL_OFFSET_EENHEDEN),
    werkdagCorrectie: z.enum(CONTRACT_MIJLPAAL_WERKDAG_CORRECTIES),
    tijdstip: z.string().regex(TIJD, 'Geef een geldige tijd op (uu:mm).').nullable(),
    voorloopDagen: z.number().int().min(0).max(60),
    deadlineAnker: z.enum(CONTRACT_MIJLPAAL_ANKERS).nullable(),
    deadlineOffsetAantal: z.number().int().min(-120).max(120),
    deadlineOffsetEenheid: z.enum(CONTRACT_MIJLPAAL_OFFSET_EENHEDEN),
    termijnDagen: z.number().int().min(0).max(120).nullable(),
    doelgroepen: z.array(z.enum(CONTRACT_MIJLPAAL_DOELGROEPEN)).min(1, 'Kies minstens een doelgroep.'),
    escalatieDoelgroepen: z.array(z.enum(CONTRACT_MIJLPAAL_DOELGROEPEN)),
    actie: z.enum(CONTRACT_MIJLPAAL_ACTIES),
    titel: z.string().trim().min(1, 'Titel is verplicht.').max(160),
    omschrijving: z.string().trim().max(1000),
    checklist: z.array(z.string().trim().min(1).max(160)).max(20),
    gesprekTypeKey: z.string().min(1).nullable(),
  })
  .superRefine((val, ctx) => {
    if (val.titel.includes('—') || val.omschrijving.includes('—')) {
      ctx.addIssue({
        code: 'custom',
        path: [val.titel.includes('—') ? 'titel' : 'omschrijving'],
        message: 'Gebruik een koppelteken met spaties in plaats van een lange streep.',
      })
    }
    if (val.actie !== CONTRACT_MIJLPAAL_ACTIE_PER_CODE[val.code]) {
      ctx.addIssue({
        code: 'custom',
        path: ['actie'],
        message: 'De actie van deze mijlpaal ligt vast. Zet de mijlpaal uit als hij niet past.',
      })
    }
    const vasteEscalatie = CONTRACT_MIJLPAAL_VASTE_ESCALATIE[val.code]
    if (vasteEscalatie) {
      if (val.deadlineAnker === null && val.termijnDagen === null) {
        ctx.addIssue({
          code: 'custom',
          path: ['termijnDagen'],
          message: 'Deze mijlpaal heeft een deadline nodig: zonder deadline komt er geen escalatie.',
        })
      }
      const ontbreekt = vasteEscalatie.filter((d) => !val.escalatieDoelgroepen.includes(d))
      if (ontbreekt.length > 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['escalatieDoelgroepen'],
          message: `De melding bij een verstreken deadline gaat bij deze mijlpaal altijd naar ${ontbreekt
            .map((d) => CONTRACT_MIJLPAAL_DOELGROEP_LABELS[d])
            .join(' en ')}.`,
        })
      }
    }
    if (val.code === 'M3' && val.checklist.filter((c) => c.trim().length > 0).length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['checklist'],
        message: 'De facilities-taak heeft een checklist nodig: die moet op de startdag zijn afgevinkt.',
      })
    }
    if (CONTRACT_MIJLPAAL_GESPREK_VERPLICHT.includes(val.code) && val.gesprekTypeKey === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['gesprekTypeKey'],
        message: 'Bij deze mijlpaal hoort een gesprek. Kies het gesprekstype.',
      })
    }
    // batch-71 kennismaking (FO-534): de motor plant dit gesprek, dus het type ligt vast.
    if (val.code === 'M2B' && val.gesprekTypeKey !== null && val.gesprekTypeKey !== CONTRACT_GESPREK_TYPE_KEYS.KENNISMAKING) {
      ctx.addIssue({
        code: 'custom',
        path: ['gesprekTypeKey'],
        message: 'De kennismaking voor de start gebruikt altijd het gesprekstype Kennismaking voor de start.',
      })
    }
    if (
      val.code === 'M9' &&
      val.gesprekTypeKey !== null &&
      val.gesprekTypeKey !== CONTRACT_GESPREK_TYPE_KEYS.EVALUATIE_INTERN
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['gesprekTypeKey'],
        message: 'Het besluit over de verlenging begint altijd met het interne besluitgesprek.',
      })
    }
  })

/**
 * Sjabloon opslaan (aanmaken en bewerken delen dit schema, zodat het form niets
 * kan sturen dat de API weigert). De regels zijn altijd de volledige set M1 t/m
 * M9: een mijlpaal uitzetten doe je met `actief: false`, niet door hem weg te
 * laten, zodat de harde regels uit de reeks nooit ongemerkt verdwijnen.
 */
export const contractMijlpaalSjabloonSchema = z
  .object({
    naam: z.string().trim().min(1, 'Naam is verplicht.').max(120),
    omschrijving: z.string().trim().max(500).nullable().optional(),
    contractType: z.string().min(1).nullable().optional(),
    entityId: z.string().min(1).nullable().optional(),
    actief: z.boolean(),
    verlengingAlleenEindreeks: z.boolean(),
    regels: z.array(sjabloonRegelSchema),
  })
  .superRefine((val, ctx) => {
    const codes = val.regels.map((r) => r.code)
    const ontbreekt = CONTRACT_MIJLPAAL_CODES.filter((c) => !codes.includes(c))
    const dubbel = codes.filter((c, i) => codes.indexOf(c) !== i)
    if (ontbreekt.length > 0 || dubbel.length > 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['regels'],
        message: 'Een sjabloon bevat elke mijlpaal (M1 t/m M9 en de kennismaking M2B) precies een keer.',
      })
    }
  })
export type ContractMijlpaalSjabloonInput = z.infer<typeof contractMijlpaalSjabloonSchema>

/** De medewerkers die samen de doelgroep Directie vormen. */
export const contractMijlpaalDirectieSchema = z.object({
  employeeIds: z.array(z.string().min(1)).max(50, 'Kies maximaal 50 medewerkers.'),
})
export type ContractMijlpaalDirectieInput = z.infer<typeof contractMijlpaalDirectieSchema>

export const CONTRACT_MIJLPAAL_SJABLOON_VELD_LABELS: Record<string, string> = {
  naam:         'Naam',
  omschrijving: 'Omschrijving',
  contractType: 'Contractvorm',
  entityId:     'BV',
  regels:       'Mijlpalen',
}
